// Solution detail content for /solutions/:slug pages
// All copy original to BuyerBeacon Pro

export const solutionDetails = {
  "website-visitor-id": {
    slug: "website-visitor-id",
    eyebrow: "Identity Solution",
    title: "Website Visitor ID",
    tagline: "Know who is on your website — even when they don’t fill out a form.",
    description: "Most of your traffic leaves without a trace. Visitor ID resolves up to 70% of anonymous US web visitors into named people with verified business emails, phone numbers, and firmographics. No forms. No friction. No guesswork about which traffic was real.",
    metric: "70%",
    metricLabel: "identification rate (US traffic)",
    bestFor: ["SMB websites", "Marketing agencies", "Lead-gen funnels"],
    features: [
      { title: "Resolve traffic, not pageviews", desc: "Get the person behind every visit — name, business email, company, role, and household details where available." },
      { title: "One-line pixel install", desc: "Paste a single snippet into your site. Identified visitors begin appearing in your dashboard within 24 hours." },
      { title: "Firmographic enrichment", desc: "Every identified visitor comes with company size, industry, revenue range, and tech stack — ready to route or score." },
      { title: "Real-time alerts", desc: "Trigger Slack, email, or webhook notifications the moment a high-value visitor lands on a pricing or product page." },
      { title: "Page-level intent", desc: "See which pages each identified visitor saw, in what order, and how long they stayed." },
      { title: "Privacy-first", desc: "All identification runs on verified, opted-in identity data. SOC 2 Type II, GDPR, and CCPA compliant." }
    ],
    howItWorks: [
      { step: "01", title: "Install the pixel", desc: "Paste a single snippet into your site head, GTM, or CMS." },
      { step: "02", title: "Identified visitors flow in", desc: "Within 24 hours, BuyerBeacon resolves anonymous traffic against our identity graph." },
      { step: "03", title: "Route to your tools", desc: "Push identified visitors to your CRM, ad platform, or sales rep — automatically." }
    ],
    faqs: [
      { q: "What is the identification rate?", a: "Typical US traffic resolves at 50–70%. Mobile-heavy and international traffic resolves lower; B2B desktop traffic resolves highest." },
      { q: "Do my visitors know?", a: "Identification uses opted-in identity data — the same compliant graph that powers commercial advertising. We provide model privacy-policy language for your site." },
      { q: "Will my site speed change?", a: "No. The pixel loads asynchronously and weighs under 5 KB." }
    ],
    related: ["audience-builder", "pulse", "buyer-intent"]
  },

  "consumer-data": {
    slug: "consumer-data",
    eyebrow: "Data Solution",
    title: "Consumer Data",
    tagline: "280M+ US adult profiles with 400+ demographic, behavioral, and intent attributes.",
    description: "License the same authoritative consumer graph that powers Fortune 500 brands — priced for SMBs and agencies. Build audiences by demographics, lifestyle, purchase behavior, life events, and in-market signals.",
    metric: "280M+",
    metricLabel: "US adult profiles",
    bestFor: ["Local services", "Retailers", "Agencies", "Direct response"],
    features: [
      { title: "400+ attributes per profile", desc: "Age, income, homeownership, lifestyle, purchase history, life-event signals, and more." },
      { title: "Hyper-local targeting", desc: "Build audiences down to a single ZIP+4 or a custom polygon around your service area." },
      { title: "Life-event triggers", desc: "Reach people the week they buy a home, have a baby, change jobs, or get married — when your offer is most relevant." },
      { title: "Suppression made easy", desc: "Upload customer lists to suppress, and target only net-new prospects." },
      { title: "Match-back ready", desc: "Every audience is keyed for clean match-back against your sales data." },
      { title: "Refresh monthly", desc: "Records are re-verified continuously — you never pay for stale data." }
    ],
    howItWorks: [
      { step: "01", title: "Define your buyer", desc: "Filter by demographics, geo, life events, and in-market signals." },
      { step: "02", title: "Preview and refine", desc: "See your audience size live as you adjust filters." },
      { step: "03", title: "Push or download", desc: "Send to ads, mail, CRM, or download a CSV in seconds." }
    ],
    faqs: [
      { q: "Are the records compliant?", a: "Yes. All data is licensed from opt-in, US-compliant sources and audited quarterly." },
      { q: "What is the minimum audience size?", a: "100 records. Most SMB campaigns run between 5,000 and 100,000." },
      { q: "Can I target by ZIP code?", a: "Yes — down to ZIP+4, neighborhood, or a custom radius around any address." }
    ],
    related: ["audience-builder", "offline-activation", "buyer-intent"]
  },

  "business-intelligence": {
    slug: "business-intelligence",
    eyebrow: "Data Solution",
    title: "Business Intelligence",
    tagline: "120M+ verified B2B contacts with firmographics, technographics, and org structure.",
    description: "Build account-based audiences with the depth your sales team actually needs. Filter by industry, revenue, headcount, tech stack, funding stage, and decision-maker role. Export to your CRM or sequence in one click.",
    metric: "120M+",
    metricLabel: "verified B2B contacts",
    bestFor: ["B2B SMBs", "Agencies", "Sales teams"],
    features: [
      { title: "Verified business emails", desc: "Every contact validated within the last 90 days. Bounce rates under 3%." },
      { title: "Decision-maker filters", desc: "Target by seniority (VP+, C-suite), department, and named-account lists." },
      { title: "Technographic targeting", desc: "Filter accounts running specific software — Salesforce, Shopify, HubSpot, and 1,500+ more." },
      { title: "Trigger events", desc: "Hiring spikes, funding rounds, office moves, and exec changes — know when accounts are buying." },
      { title: "Org charts on demand", desc: "See the full reporting structure of any target account." },
      { title: "Direct dials included", desc: "Phone numbers attached to a growing share of records for callable outbound." }
    ],
    howItWorks: [
      { step: "01", title: "Define your ICP", desc: "Industry, size, revenue, geo, tech, and trigger events." },
      { step: "02", title: "Stack the contacts", desc: "Choose how many decision-makers per account — 1 to 25." },
      { step: "03", title: "Send to sales", desc: "Export to CRM or sequence in your outbound tool of choice." }
    ],
    faqs: [
      { q: "How fresh is the data?", a: "Emails are verified within 90 days; firmographics refresh monthly. Bad records are credited." },
      { q: "Can I exclude existing customers?", a: "Yes — upload a suppression list and we filter every audience automatically." },
      { q: "Is GDPR-compliant data available?", a: "Yes. EU records follow GDPR; US records follow CCPA — with the appropriate lawful basis applied to each." }
    ],
    related: ["audience-builder", "pulse", "buyer-intent"]
  },

  "audience-builder": {
    slug: "audience-builder",
    eyebrow: "Activation Solution",
    title: "Audience Builder",
    tagline: "Drag, drop, filter, ship. The fastest way to go from idea to activated audience.",
    description: "Build hyper-targeted audiences from any combination of consumer, business, intent, and visitor data. Preview audience size in real time. Push to any channel in one click.",
    metric: "60K+",
    metricLabel: "prebuilt and custom segments",
    bestFor: ["Marketing managers", "Agencies", "Demand gen leads"],
    features: [
      { title: "Visual filter builder", desc: "Drag-and-drop AND/OR logic with live audience size as you build." },
      { title: "Lookalike modeling", desc: "Upload your best customers — we find 50,000 more who look like them." },
      { title: "Saved audiences", desc: "Reuse and refresh audiences automatically as new records enter the graph." },
      { title: "Suppression layers", desc: "Combine customer, do-not-mail, and competitor-supplied lists in one rule set." },
      { title: "One-click activation", desc: "Push to Meta, Google, TikTok, HubSpot, Salesforce, Klaviyo, and 200+ more." },
      { title: "Audience score", desc: "Beacon Score ranks every segment by buyer readiness so you prioritize spend correctly." }
    ],
    howItWorks: [
      { step: "01", title: "Choose your data source", desc: "Consumer, B2B, visitors, intent — or any mix." },
      { step: "02", title: "Apply filters", desc: "Demographics, geo, behavior, technographics, intent signals." },
      { step: "03", title: "Activate everywhere", desc: "Push to ads, email, CRM, or direct mail in one click." }
    ],
    faqs: [
      { q: "Do I need a data team?", a: "No. The builder is visual — marketers without SQL build audiences in minutes." },
      { q: "Can my agency manage multiple client audiences?", a: "Yes. The Authority plan includes multi-workspace separation so each client’s data stays isolated." },
      { q: "How does pricing work for big audiences?", a: "You pay per record activated, not per record viewed — so previewing is free." }
    ],
    related: ["consumer-data", "business-intelligence", "offline-activation"]
  },

  "offline-activation": {
    slug: "offline-activation",
    eyebrow: "Activation Solution",
    title: "Offline Activation",
    tagline: "Trigger postcards and letters from digital intent — mailed within 48 hours.",
    description: "Web visitors who don’t convert online don’t have to slip away. Offline Activation drops a printed postcard or letter in their mailbox — powered by the same identity graph that resolves your traffic. SMBs see direct-mail response rates of 4–9% when triggered by intent.",
    metric: "48hr",
    metricLabel: "average mail-drop time",
    bestFor: ["Local services", "Home improvement", "Auto dealers", "Real estate agents"],
    features: [
      { title: "Trigger from any signal", desc: "Anonymous visit, abandoned form, intent spike, life event — any signal can launch a mailer." },
      { title: "Done-for-you creative", desc: "Pick a template, drop in your offer, we handle print and postage." },
      { title: "Geo-fenced campaigns", desc: "Restrict drops to your service radius so you never spend on leads you can’t serve." },
      { title: "Variable data printing", desc: "Personalize first name, neighborhood, and offer on every piece." },
      { title: "QR + UTM tracking", desc: "Every mailer carries a trackable QR or shortlink so you know exactly which prospect responded." },
      { title: "Match-back attribution", desc: "We attribute closed sales back to specific mail drops automatically." }
    ],
    howItWorks: [
      { step: "01", title: "Pick a trigger", desc: "Web visit, audience match, or intent spike." },
      { step: "02", title: "Choose creative", desc: "Template gallery or upload your own." },
      { step: "03", title: "We print and mail", desc: "Pieces drop within 48 hours of the trigger event." }
    ],
    faqs: [
      { q: "What’s the minimum drop size?", a: "100 pieces per drop. Smaller pilots welcome on the Authority plan." },
      { q: "What does it cost per piece?", a: "Pricing starts around $0.72 per postcard all-in, including data, print, and postage." },
      { q: "Can I run trigger-based and one-time campaigns side-by-side?", a: "Yes — schedule recurring trigger drops plus seasonal blasts from the same dashboard." }
    ],
    related: ["consumer-data", "audience-builder", "website-visitor-id"]
  },

  "pulse": {
    slug: "pulse",
    eyebrow: "Real-Time Solution",
    title: "Beacon Pulse",
    tagline: "Live signals streamed to your CRM the moment they happen.",
    description: "Pulse is the real-time layer of the BuyerBeacon platform. It watches every record in your audiences for buying triggers — intent spikes, job changes, web visits, life events — and pushes those signals to your CRM or sales tool the second they fire.",
    metric: "Live",
    metricLabel: "signal delivery (< 60 sec)",
    bestFor: ["Sales-led SMBs", "Agencies running outbound", "High-velocity teams"],
    features: [
      { title: "Sub-minute delivery", desc: "From signal fire to your CRM in under 60 seconds." },
      { title: "Sales-ready alerts", desc: "Slack DMs, email digests, or webhook pushes the moment a tracked account spikes." },
      { title: "Smart prioritization", desc: "Beacon Score elevates the signals worth interrupting your reps for — the rest queue silently." },
      { title: "Account watchlists", desc: "Watch any account, contact, or audience for changes — alert the moment they’re ready." },
      { title: "Bi-directional CRM sync", desc: "Pulse listens for deal-stage changes in your CRM and feeds wins back into the model." },
      { title: "No engineering required", desc: "Connect Salesforce, HubSpot, or Pipedrive in two clicks." }
    ],
    howItWorks: [
      { step: "01", title: "Define your watchlist", desc: "Choose audiences, accounts, or domains to monitor." },
      { step: "02", title: "Pick signals", desc: "Web visits, intent spikes, job changes, funding events, technographic shifts." },
      { step: "03", title: "Route the alert", desc: "Slack, email, CRM record, or custom webhook — your call." }
    ],
    faqs: [
      { q: "How is Pulse different from Visitor ID?", a: "Visitor ID resolves anonymous traffic. Pulse watches resolved records for ongoing changes and triggers alerts." },
      { q: "Can I throttle alerts?", a: "Yes — rate limits, score thresholds, and digest modes are all configurable per watchlist." },
      { q: "What CRMs does Pulse support natively?", a: "Salesforce, HubSpot, Pipedrive, Close, Zoho, Go High Level, and Copper. Anything else via webhook." }
    ],
    related: ["website-visitor-id", "buyer-intent", "audience-builder"]
  },

  "buyer-intent": {
    slug: "buyer-intent",
    eyebrow: "Intelligence Solution",
    title: "Buyer Intent",
    tagline: "40,000+ intent categories. Know who is in-market — before your competitors do.",
    description: "Buyer Intent surfaces the people and accounts actively researching products like yours — across thousands of intent categories, refreshed daily. Layer intent on top of any audience to make sure your spend goes only to ready buyers.",
    metric: "40K+",
    metricLabel: "intent categories",
    bestFor: ["Local services", "B2B sales", "Agencies", "Retail"],
    features: [
      { title: "40,000+ categories", desc: "Every product, service, and topic you can name has an intent feed behind it." },
      { title: "Daily refresh", desc: "Yesterday’s intent is yesterday’s leads. Pulse refreshes every 24 hours." },
      { title: "Score-based ranking", desc: "Beacon Score ranks each in-market person or account on a 0–100 scale of readiness." },
      { title: "Surge alerts", desc: "Be notified when intent spikes in your market or geographic territory." },
      { title: "Competitor signals", desc: "Surface contacts evaluating your named competitors so your team can intervene." },
      { title: "Local-service feeds", desc: "Verticals like roofing, HVAC, dental, and legal have dedicated category packs." }
    ],
    howItWorks: [
      { step: "01", title: "Choose intent categories", desc: "Pick from prebuilt packs or build a custom intent profile." },
      { step: "02", title: "Overlay your geo or ICP", desc: "Combine intent with your service area or target accounts." },
      { step: "03", title: "Activate ready buyers", desc: "Push the resulting audience to ads, mail, or sales." }
    ],
    faqs: [
      { q: "Where does intent data come from?", a: "A network of opt-in publishers, search behavior, content engagement, and form submissions — anonymized and aggregated." },
      { q: "Is intent data accurate for SMBs?", a: "Yes. Local-service verticals especially benefit — we maintain dedicated category packs for roofing, HVAC, plumbing, dental, and more." },
      { q: "How often does intent decay?", a: "Most signals decay within 30 days; some life-event signals last 90+ days. Pulse handles freshness automatically." }
    ],
    related: ["pulse", "audience-builder", "website-visitor-id"]
  },

  "authorized-partner": {
    slug: "authorized-partner",
    eyebrow: "Partner Program",
    title: "Authorized Partner",
    tagline: "White-label our entire platform under your brand. Keep 80%+ margins from day one.",
    description: "For agencies, consultants, and resellers who want to own the data-intelligence relationship with their clients. White-label the platform, set your own pricing, and keep the lion’s share of revenue — with full BuyerBeacon support behind the scenes.",
    metric: "80%+",
    metricLabel: "partner margins",
    bestFor: ["Marketing agencies", "Consultants", "Resellers"],
    features: [
      { title: "Full white-label", desc: "Custom domain, your logo, your color palette — the platform appears as yours." },
      { title: "Multi-client workspaces", desc: "Manage every client account in one console with full data isolation." },
      { title: "Set your own pricing", desc: "Markup is yours to keep. Bill clients monthly, annually, or by the campaign." },
      { title: "Co-branded onboarding", desc: "Personal onboarding sessions with a BuyerBeacon strategist — introduced as your team member." },
      { title: "Roadmap influence", desc: "Authority partners get quarterly roadmap input and early access to new features." },
      { title: "Marketing asset library", desc: "Decks, one-pagers, email sequences, and case studies you can private-label and ship." }
    ],
    howItWorks: [
      { step: "01", title: "Apply", desc: "Quick application + brief partner-fit conversation." },
      { step: "02", title: "Brand and provision", desc: "We set up your white-label environment and onboard your team." },
      { step: "03", title: "Ship to clients", desc: "Bring on clients with co-branded assets, training, and a dedicated partner manager." }
    ],
    faqs: [
      { q: "Do I have to commit to a volume?", a: "No. Authority partners onboard at their own pace." },
      { q: "How is billing handled?", a: "You bill your clients. BuyerBeacon bills you at the wholesale rate — difference is your margin." },
      { q: "Can I sell other tools alongside BuyerBeacon?", a: "Yes. No exclusivity required." }
    ],
    related: ["audience-builder", "pulse", "business-intelligence"]
  }
};

export const allSolutionSlugs = Object.keys(solutionDetails);
